<template>
  <article class="legal-page">
    <header class="section legal-page__hero">
      <p>Rechtliches</p>
      <h1>{{ content.title }}</h1>
      <span>{{ content.intro }}</span>
    </header>

    <div class="section legal-page__body">
      <section
        v-for="section in content.sections"
        :key="section.title"
        class="legal-page__section"
      >
        <h2>{{ section.title }}</h2>
        <div class="legal-page__content">
          <p v-for="paragraph in section.paragraphs" :key="paragraph.text">
            {{ paragraph.text }}
            <a
              v-if="paragraph.link"
              :href="paragraph.link.href"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ paragraph.link.label }}
            </a>
          </p>
        </div>
      </section>
    </div>
  </article>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";
import { formatEmailAddress, personalEmail } from "@/utils/contact";

interface LegalLink {
  label: string;
  href: string;
}

interface LegalParagraph {
  text: string;
  link?: LegalLink;
}

interface LegalSection {
  title: string;
  paragraphs: LegalParagraph[];
}

interface LegalContent {
  title: string;
  intro: string;
  sections: LegalSection[];
}

const owner = {
  name: "Timo Rothdeutsch",
  addressLine: "Kram 18, 51491 Overath, Deutschland",
  email: formatEmailAddress(personalEmail),
};

const hostingProvider =
  "STRATO GmbH, Otto-Ostrowski-Straße 7, 10249 Berlin, Deutschland";

export default defineComponent({
  name: "LegalPage",
  props: {
    page: {
      type: String as PropType<"impressum" | "datenschutz">,
      required: true,
    },
  },
  setup(props) {
    const content = computed<LegalContent>(() => {
      if (props.page === "datenschutz") {
        return {
          title: "Datenschutz",
          intro:
            "Diese Hinweise beschreiben, welche Daten beim Abruf dieser Website technisch anfallen können und was bei einer Kontaktaufnahme verarbeitet wird.",
          sections: [
            {
              title: "Verantwortlicher",
              paragraphs: [
                {
                  text: `${owner.name}, ${owner.addressLine}.`,
                },
                {
                  text: `E-Mail: ${owner.email}`,
                },
              ],
            },
            {
              title: "Bereitstellung der Website",
              paragraphs: [
                {
                  text: "Ich setze auf dieser Website kein eigenes Tracking, keine Analyse-Tools und kein Kontaktformular ein.",
                },
                {
                  text: `Die Website wird auf einem eigenen Linux-Server betrieben, der bei ${hostingProvider} gehostet wird.`,
                },
                {
                  text: "Beim bloßen Aufruf einer Website fallen technisch notwendige Zugriffsdaten beim Hostinganbieter an. Dazu können insbesondere IP-Adresse, Datum und Uhrzeit des Zugriffs, angefragte Seite oder Datei, Referrer, Browser- und Betriebssysteminformationen, Datenmenge und Abrufstatus gehören.",
                },
                {
                  text: "Diese Daten sind erforderlich, damit die Website ausgeliefert, stabil betrieben und gegen technische Angriffe abgesichert werden kann. Eine eigene Auswertung zu Marketing- oder Trackingzwecken findet nicht statt.",
                },
                {
                  text: "Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Das berechtigte Interesse liegt im sicheren und zuverlässigen Betrieb dieser Website.",
                },
                {
                  text: "Reguläre Webserver-Logs werden spätestens nach 7 Tagen gelöscht. Eine längere Speicherung erfolgt nur im Einzelfall, wenn dies zur Aufklärung oder Abwehr eines konkreten Sicherheitsvorfalls erforderlich ist.",
                },
              ],
            },
            {
              title: "Kontaktaufnahme",
              paragraphs: [
                {
                  text: "Wenn du mir eine E-Mail schreibst, nutze ich deine Angaben zur Bearbeitung der Anfrage und zur weiteren Kommunikation.",
                },
                {
                  text: "Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO, wenn die Anfrage auf eine Zusammenarbeit oder ein Vertragsverhältnis gerichtet ist. In allen anderen Fällen ist Art. 6 Abs. 1 lit. f DSGVO die Rechtsgrundlage.",
                },
                {
                  text: "Die Daten werden gelöscht, sobald die Anfrage abgeschlossen ist und keine gesetzlichen Aufbewahrungspflichten bestehen.",
                },
              ],
            },
            {
              title: "Externe Links",
              paragraphs: [
                {
                  text: "Diese Website verlinkt auf externe Angebote, insbesondere LinkedIn, vecura.io und die LinkedIn-Unternehmensseite von Vecura. Beim Öffnen dieser Links gelten die Datenschutzinformationen der jeweiligen Anbieter.",
                },
              ],
            },
            {
              title: "Cookies und Tracking",
              paragraphs: [
                {
                  text: "Diese Website setzt nach aktuellem Stand keine Analyse-Cookies, kein eigenes Tracking und keine eingebetteten Social-Media-Plugins ein.",
                },
              ],
            },
            {
              title: "Deine Rechte",
              paragraphs: [
                {
                  text: "Du hast im Rahmen der DSGVO insbesondere Rechte auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit und Widerspruch.",
                },
                {
                  text: "Wenn du der Ansicht bist, dass die Verarbeitung personenbezogener Daten gegen Datenschutzrecht verstößt, kannst du dich bei einer Datenschutzaufsichtsbehörde beschweren. Für Nordrhein-Westfalen ist die LDI NRW zuständig: ",
                  link: {
                    label: "ldi.nrw.de/kontakt/ihre-beschwerde",
                    href: "https://www.ldi.nrw.de/kontakt/ihre-beschwerde",
                  },
                },
              ],
            },
          ],
        };
      }

      return {
        title: "Impressum",
        intro:
          "Anbieterkennzeichnung und Kontaktangaben für diese persönliche Website.",
        sections: [
          {
            title: "Angaben gemäß § 5 DDG",
            paragraphs: [
              {
                text: owner.name,
              },
              {
                text: "Kram 18",
              },
              {
                text: "51491 Overath",
              },
              {
                text: "Deutschland",
              },
            ],
          },
          {
            title: "Kontakt",
            paragraphs: [
              {
                text: `E-Mail: ${owner.email}`,
              },
            ],
          },
          {
            title: "Verantwortlich für den Inhalt",
            paragraphs: [
              {
                text: `${owner.name}, ${owner.addressLine}.`,
              },
            ],
          },
          {
            title: "Hinweis zu Vecura",
            paragraphs: [
              {
                text: "Inhalte zu Vecura auf dieser Website beschreiben meine Rolle als Mitgründer & COO und verlinken ergänzend auf vecura.io.",
              },
            ],
          },
        ],
      };
    });

    return {
      content,
    };
  },
});
</script>

<style scoped>
.legal-page {
  min-height: calc(100vh - 76px);
  color: var(--color-ink);
}

.legal-page__hero {
  border-bottom: 1px solid var(--color-line);
  background: var(--color-paper);
  padding-top: 78px;
  padding-bottom: 62px;
}

.legal-page__hero p {
  margin: 0 0 12px;
  color: var(--color-muted);
  font-size: 0.82rem;
  font-weight: 900;
  text-transform: uppercase;
}

h1 {
  max-width: 900px;
  margin: 0;
  color: var(--color-ink);
  font-size: clamp(2.6rem, 5vw, 4.3rem);
  line-height: 1;
}

.legal-page__hero span {
  display: block;
  max-width: 760px;
  margin-top: 22px;
  color: var(--color-muted);
  font-size: 1.08rem;
  line-height: 1.7;
}

.legal-page__body {
  display: grid;
  gap: 0;
  background: var(--color-paper-strong);
}

.legal-page__section {
  display: grid;
  grid-template-columns: minmax(220px, 0.35fr) minmax(0, 1fr);
  gap: 34px;
  border-bottom: 1px solid var(--color-line);
  padding: 28px 0;
}

h2 {
  margin: 0;
  color: var(--color-ink);
  font-size: 1.12rem;
}

.legal-page__section p {
  max-width: 760px;
  margin: 0;
  color: var(--color-muted);
  line-height: 1.7;
}

.legal-page__content {
  min-width: 0;
}

.legal-page__section p + p {
  margin-top: 10px;
}

.legal-page__section a {
  color: var(--color-ink);
  font-weight: 850;
  text-decoration-color: rgba(20, 20, 20, 0.28);
  text-underline-offset: 4px;
  transition: color 180ms ease, text-decoration-color 180ms ease;
}

.legal-page__section a:hover {
  color: var(--color-yellow);
  text-decoration-color: currentColor;
}

@media (max-width: 720px) {
  .legal-page__hero {
    padding-top: 48px;
  }

  .legal-page__section {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

@media (max-width: 760px) {
  .legal-page {
    min-height: auto;
  }

  .legal-page__hero {
    padding-top: 44px;
    padding-bottom: 44px;
  }

  h1 {
    font-size: 2.35rem;
    line-height: 1.03;
  }

  .legal-page__hero span {
    margin-top: 16px;
    font-size: 1rem;
    line-height: 1.62;
  }

  .legal-page__body {
    gap: 0;
  }

  .legal-page__section {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 24px 0;
  }

  .legal-page__section p {
    max-width: none;
    font-size: 0.96rem;
    line-height: 1.62;
  }
}
</style>
